import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import JobWorkExemptPanel from "@/components/JobWorkExemptPanel";
import { UsdBaseQtyHint } from "@/components/UsdBaseQtyHint";
import { ExcelPriceUpload } from "@/components/ExcelPriceUpload";
import { ExpiringRenewPanel } from "@/components/ExpiringRenewPanel";
import { auth } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Home, Loader2, Search, Plus, CheckCircle2, XCircle,
  Clock, IndianRupee, DollarSign, Boxes, Users, Send, ShieldCheck,
  ReceiptText, FileSpreadsheet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt = (v) => v == null ? "—" : (typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : String(v));

// Mirror of backend `_scale_to_sap_2dp` — SAP BYD only stores 2dp on
// `<Rate><DecimalValue>`, so a `$0.0234/pc` price gets multiplied out
// to `$23.40 per 1000 pc` before write. Returns null when scaling isn't
// needed (amount already fits 2dp). Client-side we use it only to show
// a preview; the backend re-computes authoritatively.
const scaleForSap = (amount, baseQty) => {
  const amt = Number(amount);
  const bq  = Number(baseQty || 1);
  if (!Number.isFinite(amt) || amt <= 0) return null;
  if (Math.abs(Math.round(amt * 100) - amt * 100) < 1e-7 && amt >= 0.005) return null;
  for (const s of [10, 100, 1000, 10000]) {
    const scaled = Math.round(amt * s * 100) / 100;
    if (Math.abs(scaled - amt * s) < 1e-7 * s && scaled >= 0.005) {
      return { amount: scaled, baseQty: bq * s, scale: s };
    }
  }
  return { amount: Math.round(amt * 10000 * 100) / 100, baseQty: bq * 10000, scale: 10000 };
};
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDate = (iso) => {
  if (!iso) return "—";
  if (typeof iso === "string" && iso.startsWith("9999")) return "no end";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return typeof iso === "string" ? iso.slice(0, 10) : "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = MONTHS_SHORT[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
};

/** Small status pill used in the drafts panel. */
function StatusPill({ status }) {
  const map = {
    pending_l1:        ["bg-amber-50 text-amber-800 border-amber-200",  "L1 pending"],
    pending_l2:        ["bg-amber-50 text-amber-800 border-amber-200",  "L2 pending"],
    pending_sap_write: ["bg-blue-50 text-blue-800 border-blue-200",     "queued for SAP"],
    sap_written:      ["bg-emerald-50 text-emerald-800 border-emerald-200", "written to SAP ✓"],
    sap_error:        ["bg-red-50 text-red-800 border-red-200",         "SAP error"],
    rejected:          ["bg-red-50 text-red-800 border-red-200",         "rejected"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600 border-slate-200", status];
  return <span className={`inline-flex px-2 py-0.5 text-fiori-caption font-semibold rounded-sm border ${cls}`}>{label}</span>;
}

export default function ListPrices() {
  const nav = useNavigate();
  const [mode, setMode] = useState("supplier");   // supplier | item
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);   // { code, name/desc }
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const isAdminUser = auth.user()?.role === "Admin";
  const [pendingMine, setPendingMine] = useState([]);
  const [pendingSelected, setPendingSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [freshness, setFreshness] = useState(null);   // { primary:{age_minutes,last_synced_at}, ... }
  const [refreshing, setRefreshing] = useState(false);
  // Per-row inline-edit state keyed by spec_object_id:
  //   { newAmount: "", validFrom: "YYYY-MM-DD", validTo: "YYYY-MM-DD"|"", checked: bool }
  const [edits, setEdits] = useState({});
  const [submittingBulk, setSubmittingBulk] = useState(false);

  const rowKey = (r) => r.spec_object_id || `new-${r.supplier_code}-${r.material_code}`;

  const today = new Date().toISOString().slice(0, 10);
  const patchEdit = (id, patch) =>
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || { newAmount: "", validFrom: today, validTo: "", checked: false }), ...patch } }));

  const loadSelection = useCallback(async (code) => {
    if (!code) { setRows([]); setDrafts([]); setEdits({}); return; }
    setLoading(true);
    try {
      const endpoint = mode === "supplier"
        ? `${API}/list-prices/by-supplier/${encodeURIComponent(code)}`
        : `${API}/list-prices/by-item/${encodeURIComponent(code)}`;
      const r = await axios.get(endpoint, { headers: authHeaders() });
      const sap = r.data.sap_prices || [];
      setRows(sap);
      setDrafts(r.data.pending_drafts || []);
      // Seed an empty edit slot per row so inputs are controlled from the start.
      // Un-priced rows (from GPurci history) use a composite key.
      const seed = {};
      const todayStr = new Date().toISOString().slice(0, 10);
      for (const row of sap) {
        const k = row.spec_object_id || `new-${row.supplier_code}-${row.material_code}`;
        seed[k] = {
          newAmount: "",
          validFrom: todayStr,
          validTo: "",
          checked: false,
        };
      }
      setEdits(seed);
      setSelected({
        code,
        display: mode === "supplier" ? r.data.supplier_name : r.data.material_desc,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  const loadPendingMine = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/list-prices/drafts?awaiting_me=true&limit=20`,
                                 { headers: authHeaders() });
      setPendingMine(r.data.rows || []);
    } catch (e) {
      // Silently ignore — the queue is a bonus panel.
    }
  }, []);

  useEffect(() => { loadPendingMine(); }, [loadPendingMine]);

  const loadFreshness = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/list-prices/freshness`,
                                 { headers: authHeaders() });
      setFreshness(r.data || null);
    } catch (e) { /* freshness is a bonus indicator */ }
  }, []);

  useEffect(() => { loadFreshness(); }, [loadFreshness]);

  const refreshFromSap = async () => {
    // Kicks off the backend `refresh-from-sap` job which pulls items,
    // suppliers, supplier-names, price specs, and the analytics mirror.
    // The endpoint returns 200 in <1s (background task); we poll
    // `/list-prices/refresh-status` every 3 s until the job flips
    // `running: false` so a full ~2-min refresh no longer hits the
    // Cloudflare 60 s ingress cut-off.
    setRefreshing(true);
    let started = false;
    try {
      const r = await axios.post(`${API}/list-prices/refresh-from-sap`, {},
                                  { headers: authHeaders(), timeout: 20000 });
      started = true;
      if (r.data?.already_running) {
        toast.info(`SAP refresh already running (started by ${r.data.started_by || "someone"}) — waiting for it to finish…`);
      } else {
        toast.info("SAP refresh started — polling for completion (~2 min)…");
      }
      // Poll status every 3 s, max 5 min.
      const deadline = Date.now() + 5 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) {
          toast.error("SAP refresh took longer than 5 min — check backend logs. Data may still complete in the background.");
          break;
        }
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const s = await axios.get(`${API}/list-prices/refresh-status`,
                                      { headers: authHeaders(), timeout: 10000 });
          if (!s.data?.running) {
            const res = s.data?.result || {};
            const items = res.items?.rows_synced ?? res.items?.count ?? "—";
            const specs = res.price_specs_active?.count ?? res.price_specs?.rows_synced ?? "—";
            const sups = res.suppliers?.rows_synced ?? res.suppliers?.count ?? "—";
            const err = res.items?.error || res.suppliers?.error || res.price_specs_active?.error || res.price_specs?.error;
            if (err) {
              toast.error(`SAP refresh finished with errors — ${err}`, { duration: 12000 });
            } else {
              toast.success(`SAP refresh complete · items: ${items}, suppliers: ${sups}, prices: ${specs}`);
            }
            loadFreshness();
            if (selected) loadSelection(selected.code);
            break;
          }
        } catch (pe) {
          // polling error is not fatal — keep waiting
          console.warn("refresh-status poll error", pe);
        }
      }
    } catch (e) {
      if (!started) {
        toast.error(e?.response?.data?.detail || "Refresh from SAP failed to start");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const submitDecision = async (draftId, decision, comment = "") => {
    try {
      const r = await axios.post(`${API}/list-prices/drafts/${draftId}/decision`,
                                   { decision, comment }, { headers: authHeaders() });
      if (r.data.ok === false) {
        // Server returned 200 but flagged the write as failed —
        // surface the actionable message so the approver knows what
        // to fix (unmapped Product Category, SAP HTTP error, etc.).
        const msg = r.data.user_message
                     || r.data.message
                     || r.data.sap_note
                     || r.data.fault
                     || "See the draft audit log for the raw SAP response.";
        toast.error(msg, { duration: 15000, style: { maxWidth: 560 } });
      } else {
        toast.success(decision === "approve"
          ? (r.data.new_status === "sap_written" ? "Approved — written to SAP" : "Approved")
          : "Rejected");
      }
      loadPendingMine();
      if (selected) loadSelection(selected.code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Rows the user has ticked + filled with a valid new price. Any ticked row
  // that fails validation is highlighted at submit time — we DON'T silently
  // skip them, because that has bitten us before with people expecting all
  // ticked rows to get pushed.
  const eligibleForSubmit = rows.filter((r) => {
    const e = edits[rowKey(r)];
    return e && e.checked;
  });

  const validateEdit = (row, e) => {
    const amt = Number(e.newAmount);
    if (!(amt > 0)) return "New price must be a positive number";
    // Allow up to 4dp — backend auto-scales via base quantity to fit
    // SAP's 2dp cap on `<Rate>`. Rows with >4dp are rejected outright.
    // Use a tolerance because JS float math means `0.34 * 10000` is
    // `3400.0000000000005`, not `3400` — a strict `!==` would falsely
    // flag a valid 2-decimal amount. Matches the backend's `abs(round -
    // raw) < 1e-9` guard.
    const scaled = amt * 100;
    if (Math.abs(Math.round(scaled) - scaled) > 1e-6) return "Max 2 decimals (use Base Qty for smaller rates)";
    // Same price is allowed — buyers often submit a validity extension
    // (renewing the same rate for another period). No blocker here.
    if (!e.validFrom) return "Valid-from date is required";
    if (e.validFrom < today) return "Valid-from cannot be in the past";
    if (!e.validTo) return "Valid-to date is required";
    if (e.validTo < today) return "Valid-to cannot be in the past";
    if (e.validTo <= e.validFrom) return "Valid-to must be after valid-from";
    return null;
  };

  const toggleAll = (checked) => {
    setEdits((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        const k = rowKey(r);
        next[k] = { ...(next[k] || {}), checked };
      }
      return next;
    });
  };

  const submitBulk = async () => {
    if (eligibleForSubmit.length === 0) {
      toast.error("Tick at least one row to submit"); return;
    }
    // Validate first — bail out and highlight if anything is off.
    const invalid = eligibleForSubmit.map((r) => ({ r, err: validateEdit(r, edits[rowKey(r)]) }))
                                      .filter((x) => x.err);
    if (invalid.length) {
      toast.error(`${invalid.length} row(s) invalid · ${invalid[0].err}`);
      return;
    }
    setSubmittingBulk(true);
    let ok = 0, fail = 0, corrected = 0;
    for (const row of eligibleForSubmit) {
      const e = edits[rowKey(row)];
      try {
        const resp = await axios.post(`${API}/list-prices/drafts`, {
          supplier_code: row.supplier_code,
          material_code: row.material_code,
          amount: Number(e.newAmount),
          currency_code: (row.currency_code || "INR").toUpperCase(),
          base_quantity: Number(row.base_quantity || 1),
          unit_code: (row.unit_code || "EA").toUpperCase(),
          valid_from: e.validFrom,
          valid_to: e.validTo,
          notes: `Bulk revise from ${fmt(row.amount)} ${row.currency_code} → ${e.newAmount}`,
        }, { headers: authHeaders() });
        ok += 1;
        if (resp.data?.uom_correction?.applied) corrected += 1;
      } catch (err) {
        fail += 1;
        console.warn("draft submit failed", rowKey(row), err?.response?.data);
      }
    }
    setSubmittingBulk(false);
    if (ok > 0) {
      const suffix = corrected > 0
        ? ` · ${corrected} auto-corrected UoM from SAP master`
        : "";
      toast.success(`${ok} draft(s) submitted for approval${suffix}`);
    }
    if (fail > 0) toast.error(`${fail} row(s) failed — check the pending panel`);
    if (selected) loadSelection(selected.code);
    loadPendingMine();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                    className="text-white hover:bg-white/10"
                    data-testid="lp-back-sap">
              <ArrowLeft size={16} className="mr-1" /> SAP Workspace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    className="text-white hover:bg-white/10"
                    data-testid="lp-back-home">
              <Home size={16} className="mr-1" /> Home
            </Button>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <div className="flex items-center gap-2">
              <IndianRupee size={18} />
              <div className="text-sm sm:text-base font-bold tracking-tight">List Price Management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {freshness?.primary?.age_minutes != null && (
              <span className="hidden sm:inline text-fiori-caption font-semibold bg-white/10 border border-white/20 rounded-sm px-2 py-1"
                    data-testid="lp-freshness-chip">
                SAP {freshness.primary.age_minutes < 60
                       ? `${freshness.primary.age_minutes}m`
                       : `${Math.round(freshness.primary.age_minutes / 60)}h`} ago
              </span>
            )}
            <Button size="sm" onClick={refreshFromSap} disabled={refreshing}
                    data-testid="lp-refresh-sap-btn"
                    className="bg-white/10 text-white border border-white/20 hover:bg-white/20">
              {refreshing ? (
                <><Loader2 size={14} className="mr-1 animate-spin" /> Refreshing…</>
              ) : (
                <><Loader2 size={14} className="mr-1 hidden" /> Refresh from SAP</>
              )}
            </Button>
            {isAdminUser && (
              <Button size="sm" onClick={() => setShowUpload(true)}
                      data-testid="lp-upload-excel-btn"
                      className="bg-white/10 text-white border border-white/20 hover:bg-white/20">
                <FileSpreadsheet size={14} className="mr-1" /> Upload Excel
              </Button>
            )}
            <Button size="sm" onClick={() => setShowNew(true)}
                    data-testid="lp-new-btn"
                    className="bg-white text-cyan-800 hover:bg-white/90">
              <Plus size={14} className="mr-1" /> New Price
            </Button>
          </div>
        </div>
      </header>

      <ExcelPriceUpload open={showUpload} onClose={() => setShowUpload(false)} onDone={() => loadPendingMine()} />
      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-5">
        {/* Awaiting-me queue — JDE-style compact table with row selection */}
        {pendingMine.length > 0 && (
          <PendingMineTable
            rows={pendingMine}
            selected={pendingSelected}
            onToggleRow={(id) => setPendingSelected((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })}
            onToggleAll={() => setPendingSelected((prev) => {
              if (prev.size === pendingMine.length) return new Set();
              return new Set(pendingMine.map((d) => d.draft_id));
            })}
            busyBulk={bulkBusy}
            onBulk={async (decision) => {
              const ids = Array.from(pendingSelected);
              if (ids.length === 0) return;
              const label = decision === "approve" ? "Approve" : "Reject";
              if (!window.confirm(`${label} ${ids.length} draft${ids.length === 1 ? "" : "s"}?`)) return;
              setBulkBusy(true);
              let ok = 0, fail = 0;
              for (const id of ids) {
                try {
                  const r = await axios.post(`${API}/list-prices/drafts/${id}/decision`,
                                             { decision }, { headers: authHeaders() });
                  if (r.data.ok === false) fail += 1; else ok += 1;
                } catch { fail += 1; }
              }
              toast[fail === 0 ? "success" : "error"](
                `${label}d ${ok}/${ids.length}${fail ? ` · ${fail} failed` : ""}`);
              setPendingSelected(new Set());
              setBulkBusy(false);
              loadPendingMine();
              if (selected) loadSelection(selected.code);
            }}
            onSingle={submitDecision}
          />
        )}

        {/* Mode picker */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-semibold text-slate-800">Look up prices by:</div>
            <div className="inline-flex bg-slate-100 rounded-sm p-1">
              <button data-testid="mode-supplier"
                      onClick={() => { setMode("supplier"); setSelected(null); setRows([]); setDrafts([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${mode === "supplier" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"}`}>
                <Users size={12} /> Supplier
              </button>
              <button data-testid="mode-item"
                      onClick={() => { setMode("item"); setSelected(null); setRows([]); setDrafts([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${mode === "item" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"}`}>
                <Boxes size={12} /> Item
              </button>
              {isAdminUser && (
              <button data-testid="lp-mode-expiring"
                      onClick={() => { setMode("expiring"); setSelected(null); setRows([]); setDrafts([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${mode === "expiring" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"}`}>
                <Clock size={12} /> Expiring soon
              </button>
              )}
              <button data-testid="mode-jobwork"
                      onClick={() => { setMode("jobwork"); setSelected(null); setRows([]); setDrafts([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${mode === "jobwork" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"}`}>
                <ShieldCheck size={12} /> Job Work items
              </button>
            </div>
          </div>
          {mode !== "jobwork" && mode !== "expiring" && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <LivePicker
                key={mode}
                mode={mode}
                onPick={(code) => { setQuery(code); loadSelection(code); }}
                selectedCode={selected?.code}
              />
            </div>
          </div>
          )}
          {selected && (
            <div className="text-xs text-slate-600">
              Showing prices for <span className="font-semibold text-slate-800">{selected.code}</span>
              {selected.display && <> · {selected.display}</>}
            </div>
          )}
        </div>

        {mode === "expiring" && <ExpiringRenewPanel onDone={() => loadPendingMine()} />}
        {mode === "jobwork" && (
          <JobWorkExemptPanel renderPicker={(onPick, k) => <LivePicker key={`jw-${k}`} mode="item" onPick={onPick} />} />
        )}

        {/* Loading skeleton — shown while the SAP-inbound merge is fetching.
            The merge is a 2-step live OData call, so the perceived wait is
            noticeable (2-5s for suppliers with many deliveries). Give the
            user a clear "we're working" signal instead of a stale table. */}
        {loading && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 flex items-center justify-center gap-3"
               data-testid="lp-loading-card">
            <Loader2 size={18} className="animate-spin text-cyan-700" />
            <div className="text-sm text-slate-700">
              <span className="font-semibold">Loading list…</span>
              <span className="text-slate-500 ml-2">Fetching SAP prices and inbound deliveries — this can take a few seconds.</span>
            </div>
          </div>
        )}

        {/* Empty state CTA — appears when the supplier/item has zero SAP
            price specs AND zero inbound-delivery rows. Guides the user to
            onboard the very first price with a prefilled drawer. */}
        {!loading && selected && rows.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-6 flex flex-col sm:flex-row items-center gap-4"
               data-testid="lp-empty-cta">
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900 mb-1">
                No SAP price data for {mode === "supplier" ? "supplier" : "item"} <span className="mono">{selected.code}</span>
                {selected.display && <> · {selected.display}</>}
              </div>
              <div className="text-xs text-amber-800">
                This {mode} has no existing list prices and no purchase history.
                Use "New Price" below to onboard the {mode === "supplier" ? "first item for this supplier" : "first supplier for this item"} — the draft will go through L1 → L2 approval and then be written to SAP.
              </div>
            </div>
            <Button size="sm" onClick={() => setShowNew(true)}
                    data-testid="lp-empty-cta-btn"
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
              <Plus size={14} className="mr-1" />
              Add first {mode === "supplier" ? "item" : "supplier"}
            </Button>
          </div>
        )}

        {/* Existing SAP prices */}
        {!loading && selected && rows.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">Items we buy · set list prices</div>
              <div className="text-xs num text-slate-500">
                {rows.length} rows
                {rows.some((r) => r.amount == null) && (
                  <span className="ml-2 text-amber-700">
                    ({rows.filter((r) => r.amount != null).length} listed · {rows.filter((r) => r.amount == null).length} PO-only)
                  </span>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lp-prices-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="w-8 px-2 py-2">
                      <input type="checkbox"
                             data-testid="lp-check-all"
                             checked={rows.length > 0 && rows.every((r) => edits[rowKey(r)]?.checked)}
                             onChange={(e) => toggleAll(e.target.checked)}
                             className="cursor-pointer" />
                    </th>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-left px-3 py-2">Material</th>
                    <th className="text-right px-3 py-2 w-28">Current list price</th>
                    <th className="text-right px-3 py-2 w-28">Last list price</th>
                    <th className="text-right px-3 py-2 w-28">Last PO price</th>
                    <th className="text-right px-3 py-2 w-36">New contract price</th>
                    <th className="text-left px-3 py-2">UoM</th>
                    <th className="text-left px-3 py-2 w-40">Valid from *</th>
                    <th className="text-left px-3 py-2 w-40">Valid to *</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const k = rowKey(r);
                    const e = edits[k] || { newAmount: "", validFrom: today, validTo: "", checked: false };
                    const isUnpriced = r.amount == null;
                    const amt = Number(e.newAmount);
                    const changePct = (e.newAmount && r.amount)
                      ? ((amt - Number(r.amount)) / Number(r.amount)) * 100
                      : null;
                    const hasNew = e.newAmount !== "";
                    return (
                      <tr key={k}
                          className={`border-t border-slate-100 ${e.checked ? "bg-cyan-50/40" : ""} ${isUnpriced ? "bg-amber-50/30" : ""}`}
                          data-testid={`lp-row-${k}`}>
                        <td className="px-2 py-2">
                          <input type="checkbox"
                                 checked={!!e.checked}
                                 onChange={(ev) => patchEdit(k, { checked: ev.target.checked })}
                                 data-testid={`lp-row-check-${k}`}
                                 className="cursor-pointer" />
                        </td>
                        <td className="px-3 py-2">
                          <div className="mono text-xs">{r.supplier_code}</div>
                          <div className="text-fiori-caption text-slate-500">{r.supplier_name}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="mono text-xs">{r.material_code}</div>
                          <div className="text-fiori-caption text-slate-500">{r.material_desc || "—"}</div>
                        </td>
                        <td className="px-3 py-2 num text-right" data-testid={`lp-current-price-${k}`}>
                          {r.current_lp ? (
                            <span className="font-semibold text-emerald-700"
                                  title={`Valid today: ${fmtDate(r.current_lp.valid_from)} → ${fmtDate(r.current_lp.valid_to)}`}>
                              {fmt(r.current_lp.amount)}
                              <span className="text-xs text-slate-500 font-normal ml-1">{r.current_lp.currency_code}</span>
                              {Number(r.current_lp.base_quantity || 1) !== 1 && (
                                <span className="block text-[10px] text-cyan-800 font-medium">
                                  = {Number((Number(r.current_lp.amount) / Number(r.current_lp.base_quantity)).toPrecision(6))}/{r.current_lp.unit_code}
                                </span>
                              )}
                              <span className="block text-[10px] text-slate-400 font-normal">till {fmtDate(r.current_lp.valid_to)}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400" title="No list price valid today">none today</span>
                          )}
                        </td>
                        <td className="px-3 py-2 num text-right">
                          {r.amount != null ? (
                            <span
                              className="font-semibold text-slate-700 cursor-help"
                              data-testid={`lp-list-price-${k}`}
                              title={
                                `List price: ${r.amount} ${r.currency_code || ""} / ` +
                                `${r.base_quantity || 1} ${r.unit_code || "EA"}` +
                                (r.valid_from ? `\nValid from: ${fmtDate(r.valid_from)}` : "") +
                                (r.valid_to   ? `\nValid to: ${fmtDate(r.valid_to)}`     : "") +
                                `\nRelease: ${
                                  r.release_status === "3" ? "Released"
                                  : r.release_status === "5" ? "Released (explicit)"
                                  : r.release_status === "1" ? "In preparation"
                                  : r.release_status || "—"
                                }`
                              }>
                              {fmt(r.amount)}
                              <span className="text-xs text-slate-500 font-normal ml-1">{r.currency_code}</span>
                              {r.valid_from && r.valid_from > new Date().toISOString().slice(0, 10) && (
                                <span className="block text-[10px] text-amber-700 font-normal" data-testid={`lp-last-future-${k}`}>starts {fmtDate(r.valid_from)}</span>
                              )}
                              {Number(r.base_quantity || 1) !== 1 && (
                                <span className="block text-[10px] text-cyan-800 font-medium"
                                      data-testid={`lp-list-price-per-unit-${k}`}>
                                  = {Number((Number(r.amount) / Number(r.base_quantity)).toPrecision(6))}/{r.unit_code || "EA"}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 num text-right">
                          {r.last_po_price ? (
                            <span
                              className="inline-flex items-center gap-1 font-semibold text-indigo-700 cursor-help"
                              data-testid={`lp-last-po-price-${k}`}
                              title={
                                `Last PO: ${r.last_po_price.amount} ${r.last_po_price.currency_code || ""} / ` +
                                `${r.last_po_price.base_quantity || 1} ${r.last_po_price.unit_code || "EA"}` +
                                (r.last_po_price.po_date ? `\nOrdered on: ${fmtDate(r.last_po_price.po_date)}` : "")
                              }>
                              <ReceiptText size={11} className="text-indigo-500" />
                              {fmt(r.last_po_price.amount)}
                              <span className="text-xs text-slate-500 font-normal">{r.last_po_price.currency_code}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 num text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            onWheel={(ev) => ev.currentTarget.blur()} onKeyDown={(ev) => { if (ev.key === "ArrowUp" || ev.key === "ArrowDown") ev.preventDefault(); }}
                            value={e.newAmount}
                            onChange={(ev) => patchEdit(k, {
                              newAmount: ev.target.value,
                              checked: ev.target.value !== "" ? true : e.checked,
                            })}
                            placeholder="—"
                            data-testid={`lp-new-price-${k}`}
                            className="h-7 w-28 ml-auto text-right num text-xs"
                          />
                          {hasNew && changePct !== null && isFinite(changePct) && (
                            <div className={`text-fiori-caption mt-0.5 font-semibold ${
                              changePct > 0 ? "text-red-600" : changePct < 0 ? "text-emerald-600" : "text-slate-400"
                            }`}>
                              {changePct > 0 ? "+" : ""}{changePct.toFixed(1)}%
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">/{r.base_quantity} {r.unit_code}</td>
                        <td className="px-3 py-2 text-xs">
                          <Input type="date"
                                 value={e.validFrom}
                                 min={today}
                                 onChange={(ev) => patchEdit(k, { validFrom: ev.target.value })}
                                 data-testid={`lp-from-${k}`}
                                 className="h-7 text-xs" />
                          <div className="text-fiori-caption text-slate-500 mt-0.5">
                            {e.validFrom ? fmtDate(e.validFrom) : "—"}
                            {!isUnpriced && r.valid_from && <span className="text-slate-400 ml-1">· was {fmtDate(r.valid_from)}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <Input type="date"
                                 value={e.validTo}
                                 min={e.validFrom || today}
                                 onChange={(ev) => patchEdit(k, { validTo: ev.target.value })}
                                 required
                                 data-testid={`lp-to-${k}`}
                                 className={`h-7 text-xs ${!e.validTo && e.checked ? "border-red-400" : ""}`} />
                          <div className="text-fiori-caption text-slate-500 mt-0.5">
                            {e.validTo ? fmtDate(e.validTo) : "—"}
                            {!isUnpriced && r.valid_to && <span className="text-slate-400 ml-1">· was {fmtDate(r.valid_to)}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {isUnpriced ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm border bg-indigo-50 text-indigo-800 border-indigo-200"
                                  title="Purchased before but no standing list-price contract yet">
                              <ReceiptText size={10} />
                              PO-only
                            </span>
                          ) : (r.release_status === "3" || r.release_status === "5") ? (
                            // SAP `ReleaseStatusCode`:
                            //   1 = In Preparation, 2 = In Approval
                            //   3 = Released, 5 = Released (explicit)
                            // Anything ≥ 3 means the price is live in SAP.
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200"
                                  title="SAP price is released and usable on POs.">
                              <CheckCircle2 size={10} />
                              Released
                            </span>
                          ) : r.release_status === "1" || r.release_status === "2" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm border bg-amber-50 text-amber-800 border-amber-200"
                                  title="Price exists in SAP but is not yet released — buyers can't use it on POs.">
                              <Clock size={10} />
                              In preparation
                            </span>
                          ) : (
                            <span className="text-slate-500">{r.release_status || "—"}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Sticky mass-submit bar — appears only when at least one row is ticked. */}
            {eligibleForSubmit.length > 0 && (
              <div className="sticky bottom-0 z-10 bg-white border-t border-slate-200 px-4 py-2 flex items-center justify-between"
                   data-testid="lp-bulk-bar">
                <div className="text-xs text-slate-600">
                  <span className="font-semibold text-slate-800">{eligibleForSubmit.length}</span> row(s) selected · each will create its own L1 → L2 draft
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"
                          onClick={() => toggleAll(false)}
                          data-testid="lp-bulk-clear"
                          className="h-8">Clear</Button>
                  <Button size="sm"
                          onClick={submitBulk}
                          disabled={submittingBulk}
                          data-testid="lp-bulk-submit"
                          className="h-8 bg-cyan-700 hover:bg-cyan-800">
                    {submittingBulk
                      ? <Loader2 size={12} className="animate-spin mr-1" />
                      : <Send size={12} className="mr-1" />}
                    Submit {eligibleForSubmit.length} for approval
                  </Button>
                </div>
              </div>
            )}
            {drafts.length > 0 && (
              <div className="border-t border-slate-100">
                <div className="px-4 py-2 bg-amber-50 text-xs font-semibold text-amber-800">
                  <Clock size={12} className="inline mr-1" /> {drafts.length} pending draft(s) for this {mode}
                </div>
                <div className="p-3 space-y-1">
                  {drafts.map((d) => (
                    <div key={d.draft_id} className="text-xs mono border border-slate-100 rounded-sm px-3 py-1.5 flex items-center gap-2 flex-wrap"
                         data-testid={`draft-row-${d.draft_id}`}>
                      <span title={d.draft_id} className="font-semibold text-slate-700">{d.short_id || d.draft_id}</span>
                      <StatusPill status={d.status} />
                      <span className="min-w-0 flex flex-col" data-testid={`draft-item-${d.draft_id}`}>
                        <span className="font-semibold text-slate-800">{d.material_code}{mode === "item" && d.supplier_code ? ` · ${d.supplier_code}` : ""}</span>
                        <span className="font-sans text-slate-500 truncate max-w-[320px]">{d.material_desc || "—"}</span>
                      </span>
                      <span className="font-sans text-slate-500" data-testid={`draft-creator-${d.draft_id}`}>
                        by <span className="text-slate-700">{(d.created_by_name || d.created_by || "—").replace(/\s*\(\d+\)\s*$/, "")}</span>
                        {d.created_at ? ` · ${fmtDate(String(d.created_at).slice(0, 10))}` : ""}
                      </span>
                      <span className="font-sans text-slate-500" data-testid={`draft-last-lp-${d.draft_id}`}>
                        Last LP: {d.last_lp
                          ? <span className="text-slate-700 num">{fmt(d.last_lp.rate)} {d.last_lp.currency_code} / {d.last_lp.unit_code} <span className="text-slate-400">({d.last_lp.current ? "till" : "ended"} {fmtDate(d.last_lp.valid_to)})</span></span>
                          : <span className="text-slate-400">none (new)</span>}
                      </span>
                      <span className="ml-auto num font-semibold">
                        {fmt(d.display_amount ?? d.amount)} {d.currency_code}
                        {" / "}
                        {(d.display_base_quantity ?? d.base_quantity) && (d.display_base_quantity ?? d.base_quantity) !== 1
                          ? `${fmt(d.display_base_quantity ?? d.base_quantity)} `
                          : ""}
                        {d.unit_code}
                      </span>
                      <span className="text-slate-500">valid {fmtDate(d.valid_from)} → {fmtDate(d.valid_to)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {showNew && <NewPriceDrawer initial={selected}
                                   mode={mode}
                                   onClose={() => setShowNew(false)}
                                   onCreated={() => { setShowNew(false); if (selected) loadSelection(selected.code); loadPendingMine(); }} />}
    </div>
  );
}

/**
 * JDE-style compact grid for "awaiting your approval" drafts.
 * Checkbox column left, tight numeric cells, per-row Approve/Reject
 * far right, bulk approve/reject header actions when N > 0 selected.
 */
function PendingMineTable({ rows, selected, onToggleRow, onToggleAll,
                             busyBulk, onBulk, onSingle }) {
  const allChecked = selected.size === rows.length && rows.length > 0;
  const someChecked = selected.size > 0 && !allChecked;
  return (
    <div className="bg-amber-50/60 border border-amber-200 rounded-sm overflow-hidden"
         data-testid="pending-mine-grid">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-amber-200 bg-amber-100/60">
        <div className="text-fiori-caption font-semibold text-amber-900 inline-flex items-center gap-1 uppercase tracking-wider">
          <ShieldCheck size={13} /> Awaiting your approval · {rows.length}
        </div>
        {selected.size > 0 && (
          <div className="inline-flex gap-1" data-testid="pending-mine-bulk">
            <Button size="sm" variant="outline"
                    className="h-6 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                    disabled={busyBulk}
                    onClick={() => onBulk("reject")}
                    data-testid="pending-mine-bulk-reject">
              <XCircle size={11} className="mr-1" /> Reject selected ({selected.size})
            </Button>
            <Button size="sm"
                    className="h-6 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={busyBulk}
                    onClick={() => onBulk("approve")}
                    data-testid="pending-mine-bulk-approve">
              {busyBulk
                ? <Loader2 size={11} className="mr-1 animate-spin" />
                : <CheckCircle2 size={11} className="mr-1" />}
              Approve selected ({selected.size})
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white border-b border-amber-200 text-fiori-caption uppercase tracking-wider text-slate-600">
            <tr>
              <th className="w-8 px-2 py-1.5 text-left">
                <input type="checkbox"
                       checked={allChecked}
                       ref={(el) => { if (el) el.indeterminate = someChecked; }}
                       onChange={onToggleAll}
                       data-testid="pending-mine-select-all" />
              </th>
              <th className="text-left px-2 py-1.5 font-semibold">Draft</th>
              <th className="text-left px-2 py-1.5 font-semibold">Stage</th>
              <th className="text-left px-2 py-1.5 font-semibold">Supplier</th>
              <th className="text-left px-2 py-1.5 font-semibold">Material</th>
              <th className="text-right px-2 py-1.5 font-semibold">Price</th>
              <th className="text-left px-2 py-1.5 font-semibold">UoM</th>
              <th className="text-left px-2 py-1.5 font-semibold">Validity</th>
              <th className="text-right px-2 py-1.5 font-semibold w-40">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const isChecked = selected.has(d.draft_id);
              const bq = d.display_base_quantity ?? d.base_quantity;
              const amt = d.display_amount ?? d.amount;
              return (
                <tr key={d.draft_id}
                    className={`border-b border-amber-100 hover:bg-amber-50 ${isChecked ? "bg-amber-100/60" : "bg-white"}`}
                    data-testid={`awaiting-${d.draft_id}`}>
                  <td className="px-2 py-1.5">
                    <input type="checkbox"
                           checked={isChecked}
                           onChange={() => onToggleRow(d.draft_id)}
                           data-testid={`pending-mine-select-${d.draft_id}`} />
                  </td>
                  <td className="px-2 py-1.5 mono font-semibold text-slate-800"
                      title={d.draft_id}>
                    {d.short_id || d.draft_id}
                  </td>
                  <td className="px-2 py-1.5">
                    <StatusPill status={d.status} />
                    {(() => {
                      const l1 = (d.approval_history || [])
                        .find((h) => (h.stage === "l1" || h.stage === "pending_l1")
                                       && h.decision === "approve");
                      if (!l1) return null;
                      return (
                        <div className="text-[10px] text-slate-500 mt-0.5 leading-tight"
                             data-testid={`l1-audit-${d.draft_id}`}
                             title={l1.comment ? `Comment: ${l1.comment}` : ""}>
                          Authorised by <span className="font-semibold text-slate-700">{l1.by_name || l1.by_emp_no}</span>
                          {l1.by_name && l1.by_emp_no && (
                            <> {" | "}<span className="mono">{l1.by_emp_no}</span></>
                          )}
                          {l1.ts && <> {" on "}{fmtDate(l1.ts)}</>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1.5 mono text-slate-800 max-w-[140px]">
                    <div className="font-semibold">{d.supplier_code}</div>
                    {d.supplier_name && (
                      <div className="text-[10px] text-slate-500 non-mono truncate"
                           title={d.supplier_name}
                           data-testid={`awaiting-supplier-name-${d.draft_id}`}>
                        {d.supplier_name}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 mono text-slate-800 max-w-[160px]">
                    <div className="font-semibold">{d.material_code}</div>
                    {d.material_desc && (
                      <div className="text-[10px] text-slate-500 non-mono truncate"
                           title={d.material_desc}
                           data-testid={`awaiting-material-desc-${d.draft_id}`}>
                        {d.material_desc}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <span className="num font-bold">{fmt(amt)}</span>
                    <span className="text-[10px] text-slate-500 ml-1">{d.currency_code}</span>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">
                    {bq && bq !== 1 ? `${fmt(bq)} ` : ""}{d.unit_code}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">
                    {fmtDate(d.valid_from)} → {fmtDate(d.valid_to)}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline"
                              className="h-6 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                              onClick={() => onSingle(d.draft_id, "reject")}
                              data-testid={`reject-${d.draft_id}`}>
                        <XCircle size={11} className="mr-1" /> Reject
                      </Button>
                      <Button size="sm"
                              className="h-6 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => onSingle(d.draft_id, "approve")}
                              data-testid={`approve-${d.draft_id}`}>
                        <CheckCircle2 size={11} className="mr-1" /> Approve
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function NewPriceDrawer({ initial, mode, onClose, onCreated }) {
  const [supplier, setSupplier] = useState(mode === "supplier" && initial ? initial.code : "");
  const [supplierName, setSupplierName] = useState(mode === "supplier" && initial ? initial.display : "");
  const [material, setMaterial] = useState(mode === "item" && initial ? initial.code : "");
  const [materialName, setMaterialName] = useState(mode === "item" && initial ? initial.display : "");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [supplierCountry, setSupplierCountry] = useState(null); // {country, foreign, required_currency}
  const [baseQty, setBaseQty] = useState("1");
  const [uom, setUom] = useState("EA");
  const [uomFromSap, setUomFromSap] = useState(false); // true when we auto-filled UoM from SAP master
  useEffect(() => {
    if (!supplier) { setSupplierCountry(null); return; }
    let off = false;
    axios.get(`${API}/list-prices/supplier-country/${encodeURIComponent(supplier)}`, { headers: authHeaders() })
      .then(({ data }) => { if (off) return; setSupplierCountry(data); if (data.required_currency) setCurrency(data.required_currency); })
      .catch(() => { if (!off) setSupplierCountry(null); });
    return () => { off = true; };
  }, [supplier]);
  const PACKET_UNITS = ["XPA", "PAK", "PKT", "PACKET", "PACK", "PCK"];
  const isPacketUom = PACKET_UNITS.includes(uom.trim().toUpperCase());
  const [uomLoading, setUomLoading] = useState(false);
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // BOM membership guard (Sep-25-2026 policy): materials that aren't
  // part of any Released BOM can't be onboarded as list prices. We
  // check the moment the user picks a material and hard-block Save
  // if it fails — the backend runs the same check as a safety net.
  const [bomStatus, setBomStatus] = useState("idle"); // idle|checking|in_bom|not_in_bom|error
  // Purchasing-view active-status guard (Sep-26-2026 policy). Mirrors
  // the same UX as the BOM guard — inline banner + disabled Submit.
  // `psStatus` values: idle | checking | active | inactive | error
  // `psLabel` holds the human status ("Blocked", "In Preparation",
  // …) so the banner can be specific instead of generic.
  const [psStatus, setPsStatus] = useState("idle");
  const [psLabel, setPsLabel] = useState("");

  // When a material is picked, pull its base UoM from SAP master data
  // (sap_items.BaseMeasureUnitCode) so the user doesn't submit `EA` for
  // a `KGM` material and hit a SAP "price unit inconsistency" fault at
  // approve-time. Silent on error — the user can still edit UoM manually.
  useEffect(() => {
    if (!material) { setUomFromSap(false); return; }
    let cancelled = false;
    setUomLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/list-prices/material-meta/${encodeURIComponent(material)}`,
          { headers: authHeaders() });
        if (cancelled) return;
        const bu = (r.data?.base_uom || "").toUpperCase().trim();
        // XPA is never allowed for list prices — use EA with Base qty instead.
        if (bu === "XPA") { setUom("EA"); setUomFromSap(false); }
        else if (bu) { setUom(bu); setUomFromSap(true); }
        else    { setUomFromSap(false); }
      } catch {
        if (!cancelled) setUomFromSap(false);
      } finally {
        if (!cancelled) setUomLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [material]);

  // BOM membership check — runs whenever material changes. The backend
  // rejects non-BOM materials at draft creation with a 409, but we
  // surface the same signal inline so the user can't submit and hit
  // a toast — Save is disabled until the check passes.
  useEffect(() => {
    if (!material) { setBomStatus("idle"); return; }
    let cancelled = false;
    setBomStatus("checking");
    (async () => {
      try {
        const r = await axios.post(
          `${API}/items/bom-check`,
          { item_codes: [material.trim()] },
          { headers: authHeaders() });
        if (cancelled) return;
        setBomStatus(r.data?.[material.trim()] ? "in_bom" : "not_in_bom");
      } catch {
        if (!cancelled) setBomStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [material]);

  // Purchasing-view active-status check — runs whenever material
  // changes. Backend rejects at draft creation with 409; we mirror
  // that signal here so Submit is disabled until Purchasing = Active.
  useEffect(() => {
    if (!material) { setPsStatus("idle"); setPsLabel(""); return; }
    let cancelled = false;
    setPsStatus("checking");
    (async () => {
      try {
        const r = await axios.post(
          `${API}/items/purchasing-check`,
          { item_codes: [material.trim()] },
          { headers: authHeaders() });
        if (cancelled) return;
        const info = r.data?.[material.trim()] || {};
        setPsStatus(info.active ? "active" : "inactive");
        setPsLabel(info.status_label || "");
      } catch {
        if (!cancelled) setPsStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [material]);

  const submit = async () => {
    if (!supplier || !material || !amount) {
      toast.error("Supplier, material and amount are required"); return;
    }
    if (bomStatus === "not_in_bom") {
      toast.error(`${material} isn't part of any Released BOM in SAP — list prices can only be raised for BOM materials.`);
      return;
    }
    if (psStatus === "inactive") {
      toast.error(`${material} — Purchasing view is ${psLabel || "not Active"} in SAP.`);
      return;
    }
    if (isPacketUom) {
      toast.error("XPA (packet) is not allowed. Use EA with Base qty — e.g. Base qty 100 = 6.5 USD means 0.065 USD/EA.");
      return;
    }
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Amount must be positive"); return; }
    // Float-safe 4dp guard — see the twin comment in `validateEdit`.
    const scaledAmt = amt * 100;
    if (Math.abs(Math.round(scaledAmt) - scaledAmt) > 1e-6) {
      toast.error("List price can have at most 2 decimals — use Base Qty for smaller rates (e.g. 6.50 per 100 EA)"); return;
    }
    if (!validFrom || !validTo) {
      toast.error("Valid-from and Valid-to dates are both required"); return;
    }
    if (validTo <= validFrom) {
      toast.error("Valid-to must be after Valid-from"); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/list-prices/drafts`, {
        supplier_code: supplier.trim(),
        material_code: material.trim(),
        amount: amt,
        currency_code: currency.toUpperCase(),
        base_quantity: Number(baseQty || 1),
        unit_code: uom.toUpperCase(),
        valid_from: validFrom,
        valid_to: validTo,
        notes: notes || null,
      }, { headers: authHeaders() });
      const corr = r.data?.uom_correction;
      if (corr?.applied) {
        toast.success(
          `Draft created — UoM auto-corrected ${corr.from} → ${corr.to} (SAP master).`,
          { duration: 8000 });
      } else {
        toast.success("Draft created, awaiting L1 approval");
      }
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create draft");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
         data-testid="lp-new-drawer">
      <div className="bg-white w-full max-w-lg rounded-sm shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="text-fiori-h2 text-slate-800">New list price</div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="lp-close">✕</Button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Supplier</Label>
              <AllPicker
                kind="supplier"
                value={supplier}
                displayName={supplierName}
                onPick={(row) => { setSupplier(row.code); setSupplierName(row.name); }}
                placeholder="Search any supplier…"
              /></div>
            <div><Label className="text-xs">Material</Label>
              <AllPicker
                kind="item"
                value={material}
                displayName={materialName}
                onPick={(row) => { setMaterial(row.code); setMaterialName(row.name); }}
                placeholder="Search any item…"
              /></div>
          </div>
          {material && bomStatus === "not_in_bom" && (
            <div className="text-xs bg-rose-50 border border-rose-300 rounded-sm px-3 py-2 text-rose-800 leading-snug"
                 data-testid="lp-not-in-bom-banner">
              <b>Blocked — {material} is not part of any Released BOM in SAP</b>{" "}
              and is not a pallet (PALL-*). A purchasing contract (list
              price) can only be raised for a BOM material or a pallet.
              Add the material to the consuming BOM in SAP BYD
              (Production → Bills of Material), or if it is a Job Work
              item ask an Admin to exempt it under the "Job Work items" tab.
            </div>
          )}
          {material && bomStatus === "checking" && (
            <div className="text-[11px] text-slate-500 flex items-center gap-1"
                 data-testid="lp-bom-checking">
              <Loader2 size={10} className="animate-spin" /> Verifying BOM membership…
            </div>
          )}
          {material && psStatus === "inactive" && (
            <div className="text-xs bg-rose-50 border border-rose-300 rounded-sm px-3 py-2 text-rose-800 leading-snug"
                 data-testid="lp-purchasing-inactive-banner">
              <b>Blocked — {material}'s Purchasing view in SAP is {psLabel || "not Active"}.</b>{" "}
              A list price can only be raised for a material whose
              Purchasing view is <b>Active</b>. Have the Purchasing view
              released in SAP BYD (Product Data → Materials → {material}
              &nbsp;→ Purchasing → Status = Active) and retry.
            </div>
          )}
          {material && psStatus === "checking" && (
            <div className="text-[11px] text-slate-500 flex items-center gap-1"
                 data-testid="lp-purchasing-checking">
              <Loader2 size={10} className="animate-spin" /> Checking Purchasing status…
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Amount (max 4 dp)</Label>
              <Input type="number" step="0.01" min="0" onWheel={(ev) => ev.currentTarget.blur()} onKeyDown={(ev) => { if (ev.key === "ArrowUp" || ev.key === "ArrowDown") ev.preventDefault(); }} value={amount}
                     onChange={(e)=>setAmount(e.target.value)}
                     placeholder="0.0234" data-testid="lp-amount-input"
                     className="num h-8" /></div>
            <div><Label className="text-xs">Currency</Label>
              <select value={currency} onChange={(e)=>setCurrency(e.target.value)}
                      data-testid="lp-currency-select" disabled={!!supplierCountry?.required_currency}
                      className={`h-8 w-full border border-slate-200 rounded-sm px-2 text-sm ${supplierCountry?.required_currency ? "bg-amber-50 border-amber-300 cursor-not-allowed" : ""}`}>
                <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
                <option>JPY</option><option>CNY</option><option>SGD</option>
              </select>
              {supplierCountry?.required_currency && (
                <div className="text-[10px] text-amber-800 mt-0.5" data-testid="lp-currency-locked-note">
                  Overseas supplier ({supplierCountry.country}) — USD only
                </div>
              )}
              <UsdBaseQtyHint currency={currency} />
            </div>
            <div><Label className="text-xs">
                UoM
                {uomLoading && <Loader2 size={10} className="inline ml-1 animate-spin text-slate-400" />}
                {uomFromSap && !uomLoading && (
                  <span className="ml-1 text-[10px] text-emerald-700 font-normal"
                        title="Locked to SAP material master — SAP only accepts this UoM for this product">
                    SAP-locked
                  </span>
                )}
              </Label>
              <Input value={uom}
                     onChange={(e) => { setUom(e.target.value); setUomFromSap(false); }}
                     readOnly={uomFromSap}
                     placeholder="EA" data-testid="lp-uom-input"
                     title={uomFromSap
                       ? "This UoM comes from the SAP material master and cannot be changed. SAP will reject any other unit."
                       : "Enter the unit of measure"}
                     className={`mono h-8 ${uomFromSap ? "border-emerald-300 bg-emerald-50 cursor-not-allowed" : ""} ${isPacketUom ? "border-rose-400 bg-rose-50" : ""}`} /></div>
          </div>
          {isPacketUom && (
            <div className="text-fiori-caption px-2 py-1.5 bg-rose-50 border border-rose-200 rounded-sm text-rose-800"
                 data-testid="lp-xpa-blocked-hint">
              <b>XPA (packet) is not allowed for list prices.</b> Use <b>EA</b> and put the packet size in <b>Base qty</b> —
              e.g. <b>Base qty 100 = 6.5 USD</b> means <b>0.065 USD/EA</b>.
            </div>
          )}
          {(() => {
            const s = scaleForSap(amount, baseQty);
            if (!s) return null;
            return (
              <div className="text-fiori-caption px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm text-amber-800"
                    data-testid="lp-sap-scale-preview">
                SAP stores at 2 decimals — will be sent as{" "}
                <b>{s.amount.toFixed(2)} {currency}</b> per{" "}
                <b>{s.baseQty} {uom}</b> (×{s.scale}). Economic rate preserved.
              </div>
            );
          })()}
          {Number(amount) > 0 && Number(baseQty || 1) !== 1 && (
            <div className="text-fiori-caption px-2 py-1 bg-cyan-50 border border-cyan-200 rounded-sm text-cyan-900"
                 data-testid="lp-per-unit-preview">
              You entered <b>{amount} {currency} per {baseQty} {uom}</b> ={" "}
              <b>{Number((Number(amount) / Number(baseQty)).toPrecision(6))} {currency} per 1 {uom}</b>.
              If {amount} is the price of ONE {uom}, set Base qty to 1.
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Base qty</Label>
              <Input type="number" min="0.0001" step="any" onWheel={(ev) => ev.currentTarget.blur()} onKeyDown={(ev) => { if (ev.key === "ArrowUp" || ev.key === "ArrowDown") ev.preventDefault(); }} value={baseQty}
                     onChange={(e)=>setBaseQty(e.target.value)}
                     data-testid="lp-baseqty-input" className="num h-8" /></div>
            <div><Label className="text-xs">Valid from *</Label>
              <Input type="date" value={validFrom} onChange={(e)=>setValidFrom(e.target.value)}
                     required data-testid="lp-validfrom-input" className="h-8" /></div>
            <div><Label className="text-xs">Valid to *</Label>
              <Input type="date" value={validTo} onChange={(e)=>setValidTo(e.target.value)}
                     required min={validFrom || undefined}
                     data-testid="lp-validto-input" className="h-8" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)}
                       placeholder="Optional context / quote reference"
                       data-testid="lp-notes-input" rows={2} /></div>

          <div className="text-xs bg-slate-50 border border-slate-100 rounded-sm px-3 py-2 text-slate-600">
            Once submitted, the draft goes to L1 → L2 for sequential approval per your supplier-category mapping. After L2 approves, an FX snapshot is captured and the price is queued for SAP write-back.
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="lp-cancel">Cancel</Button>
          <Button size="sm" onClick={submit}
                  disabled={submitting || isPacketUom || bomStatus === "not_in_bom" || bomStatus === "checking" || psStatus === "inactive" || psStatus === "checking"}
                  data-testid="lp-submit-btn">
            {submitting ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
            Submit for approval
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Live-search combobox for suppliers/items. Debounced (200ms), shows a
 * dropdown of {code, name} rows returned by the SAP price-spec property
 * cache — so every hit is guaranteed to have list-price data.
 */
function LivePicker({ mode, onPick, selectedCode }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timer = useRef(null);
  const boxRef = useRef(null);

  const endpoint = mode === "supplier" ? "search-suppliers" : "search-items";
  const placeholder = mode === "supplier"
    ? "Search supplier by code or name (e.g. S3219 or Balaji)"
    : "Search item by code or description (e.g. SCR415 or Screw)";

  // Debounced fetch. Empty query fetches the first page too, so the user
  // gets suggestions the moment the input is focused.
  const runSearch = useCallback((query) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${API}/list-prices/${endpoint}?q=${encodeURIComponent(query)}&limit=25`;
        const r = await axios.get(url, { headers: authHeaders() });
        setOpts(r.data.rows || []);
        setActiveIdx(-1);
      } catch (e) {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [endpoint]);

  useEffect(() => { runSearch(q); }, [q, runSearch]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (row) => {
    setQ(`${row.code}${row.name ? " · " + row.name : ""}`);
    setOpen(false);
    onPick(row.code);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, opts.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = activeIdx >= 0 ? opts[activeIdx] : opts[0];
      if (row) pick(row);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef} data-testid="lp-live-picker">
      <div className="flex items-center gap-2 border border-slate-200 rounded-sm bg-white px-2 h-9 focus-within:ring-2 focus-within:ring-cyan-500/40">
        <Search size={14} className="text-slate-400 shrink-0" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid="lp-picker-input"
          className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-0 text-xs"
        />
        {loading && <Loader2 size={12} className="animate-spin text-slate-400 shrink-0" />}
        {selectedCode && (
          <span className="text-fiori-caption font-semibold bg-cyan-50 text-cyan-800 px-1.5 py-0.5 rounded-sm mono shrink-0"
                data-testid="lp-picker-selected-badge">
            {selectedCode}
          </span>
        )}
      </div>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-80 overflow-y-auto"
             data-testid="lp-picker-dropdown">
          {opts.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">
              {q ? "No matches. Try a different code or name." : "Start typing to search."}
            </div>
          )}
          {opts.map((row, idx) => (
            <button
              key={row.code}
              type="button"
              onClick={() => pick(row)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 border-b border-slate-50 last:border-0 flex items-baseline gap-2 ${
                idx === activeIdx ? "bg-cyan-50" : "hover:bg-slate-50"
              }`}
              data-testid={`lp-picker-opt-${row.code}`}
            >
              <span className="mono text-xs font-semibold text-slate-800 shrink-0">{row.code}</span>
              <span className="text-xs text-slate-600 truncate">{row.name || "—"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Live-search combobox for the "New Price" drawer. Unlike LivePicker
 * (which is filtered to entities that already have list-price data),
 * this hits the `all-suppliers` / `all-items` endpoints so brand-new
 * supplier × material pairings can be onboarded even with zero inbound
 * or price history.
 */
function AllPicker({ kind, value, displayName, onPick, placeholder }) {
  const [q, setQ] = useState(value ? `${value}${displayName ? " · " + displayName : ""}` : "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const endpoint = kind === "supplier" ? "all-suppliers" : "all-items";

  const runSearch = useCallback((query) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/list-prices/${endpoint}?q=${encodeURIComponent(query)}&limit=25`,
          { headers: authHeaders() });
        setOpts(r.data.rows || []);
        setActiveIdx(-1);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [endpoint]);

  useEffect(() => { runSearch(""); /* prime dropdown */ }, [runSearch]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (row) => {
    setQ(`${row.code}${row.name ? " · " + row.name : ""}`);
    setOpen(false);
    onPick(row);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, opts.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const r = activeIdx >= 0 ? opts[activeIdx] : opts[0]; if (r) pick(r); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={`all-picker-${kind}`}>
      <div className="flex items-center gap-2 border border-slate-200 rounded-sm bg-white px-2 h-8 focus-within:ring-2 focus-within:ring-cyan-500/40">
        <Search size={12} className="text-slate-400 shrink-0" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); runSearch(e.target.value); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={`all-picker-input-${kind}`}
          className="h-7 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-0 text-xs mono"
        />
        {loading && <Loader2 size={12} className="animate-spin text-slate-400 shrink-0" />}
      </div>
      {open && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto"
             data-testid={`all-picker-drop-${kind}`}>
          {opts.length === 0 && !loading && (
            <div className="px-3 py-3 text-xs text-slate-500 text-center">
              {q ? "No matches" : "Type to search"}
            </div>
          )}
          {opts.map((row, idx) => (
            <button
              key={row.code}
              type="button"
              onClick={() => pick(row)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 border-b border-slate-50 last:border-0 flex items-baseline gap-2 ${
                idx === activeIdx ? "bg-cyan-50" : "hover:bg-slate-50"
              }`}
              data-testid={`all-picker-opt-${kind}-${row.code}`}
            >
              <span className="mono text-xs font-semibold text-slate-800 shrink-0">{row.code}</span>
              <span className="text-xs text-slate-600 truncate">{row.name || "—"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
